import { cn } from "@/lib/cn";

type SkeletonVariant = "text" | "card" | "avatar" | "list";

interface LoadingSkeletonProps {
  variant?: SkeletonVariant;
  count?: number;
  className?: string;
}

const shimmerClass =
  "animate-shimmer bg-gradient-to-r from-[var(--color-surface)] via-[var(--color-border-subtle)] to-[var(--color-surface)] bg-[length:200%_100%] rounded-[var(--radius-md)]";

function TextSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("space-y-3", className)}>
      <div className={cn(shimmerClass, "h-4 w-3/4")} />
      <div className={cn(shimmerClass, "h-4 w-full")} />
      <div className={cn(shimmerClass, "h-4 w-1/2")} />
    </div>
  );
}

function CardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "rounded-2xl bg-[var(--color-surface)] p-4 shadow-sm",
        className,
      )}
    >
      <div className={cn(shimmerClass, "mb-3 h-5 w-1/3")} />
      <div className={cn(shimmerClass, "mb-2 h-4 w-full")} />
      <div className={cn(shimmerClass, "h-4 w-2/3")} />
    </div>
  );
}

function AvatarSkeleton({ className }: { className?: string }) {
  return <div className={cn(shimmerClass, "h-10 w-10 rounded-full", className)} />;
}

function ListItemSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-3 py-3", className)}>
      <div className={cn(shimmerClass, "h-10 w-10 shrink-0 rounded-full")} />
      <div className="flex-1 space-y-2">
        <div className={cn(shimmerClass, "h-4 w-1/2")} />
        <div className={cn(shimmerClass, "h-3 w-3/4")} />
      </div>
    </div>
  );
}

const variantComponents: Record<SkeletonVariant, React.FC<{ className?: string }>> = {
  text: TextSkeleton,
  card: CardSkeleton,
  avatar: AvatarSkeleton,
  list: ListItemSkeleton,
};

export function LoadingSkeleton({
  variant = "text",
  count = 1,
  className,
}: LoadingSkeletonProps) {
  const Component = variantComponents[variant];

  return (
    <div className={cn("space-y-4", className)}>
      {Array.from({ length: count }, (_, index) => (
        <Component key={index} />
      ))}
    </div>
  );
}
